// src/components/TableOfContents.jsx

import React from "react";

/**
 * The TableOfContents can work in two modes:
 * 1) Legacy usage: pass in sectionIds, externalActiveSectionId, headingLabel
 * 2) New usage: pass in labelsMap, and it will generate a static list of section links.
 *
 * This version has the scroll spy logic removed, so there is no active link highlight
 * or intersection observer tracking. It simply renders a list of clickable anchors
 * pointing to the corresponding sections by ID.
 */

const TableOfContents = ({
  // Legacy props
  sectionIds = [],
  activeSectionId: externalActiveSectionId,
  headingLabel = (id) => id,

  // If labelsMap is provided, it takes precedence over sectionIds
  labelsMap,
}) => {
  // If labelsMap is provided, derive sectionIds from it
  const derivedSectionIds = labelsMap ? Object.keys(labelsMap) : sectionIds;

  // Decide how to generate heading text
  const finalHeadingLabel = labelsMap
    ? (id) => labelsMap[id] || id
    : headingLabel;

  // If no section IDs exist, render nothing
  if (!derivedSectionIds || derivedSectionIds.length === 0) {
    return null;
  }

  /**
   * Handle anchor clicks:
   * - Prevent default anchor behavior
   * - Smoothly scroll the target into view
   * - Replace the URL hash so no new history entry is created
   */
  const handleAnchorClick = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    // Use replaceState so the URL changes to #section but doesn't add a new history entry
    window.history.replaceState(null, "", `#${id}`);
  };

  // Render a simple Table of Contents with custom click (no new history entry).
  return (
    <nav className="toc sticky-toc" aria-label="Table of contents">
      <h2>Table of Contents</h2>
      <ul>
        {derivedSectionIds.map((id) => (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={(e) => handleAnchorClick(e, id)}
            >
              {finalHeadingLabel(id)}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default TableOfContents;